import React, { useState, useEffect } from 'react';
import { dataService } from '../../services/dataService';
import { Card } from '../Card';
import { School, CsvImport, District } from '../../data/types';
import { toast } from 'react-toastify';
import PageContainer from '../layouts/PageContainer';

const SuperAdminSchoolManagementScreen: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<'overview' | 'import'>('overview');
  
  // School data state
  const [schools, setSchools] = useState<School[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [csvImports, setCsvImports] = useState<CsvImport[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'error', 'success'
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  
  // Form state
  const [formSchool, setFormSchool] = useState({
    id: '',
    name: '',
    district: '',
    address: '',
    logo: '' as string | undefined
  });

  // Fetch data on component mount
  const fetchData = async () => {
    try {
      setLoading(true);
      const [schoolsData, importsData, districtsData] = await Promise.all([
        dataService.getSchools(),
        dataService.getCsvImports(),
        dataService.getDistricts()
      ]);
      
      setSchools(schoolsData);
      setDistricts(districtsData);
      setCsvImports(importsData.filter(imp => 
        imp.type === 'Schools' || imp.type === 'Teachers' || imp.type === 'Students'
      ));
    } catch (error) {
      console.error('Error fetching school management data:', error);
      toast.error('Failed to load school data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle add school
  const handleAddSchool = async () => {
    try {
      if (!formSchool.name || !formSchool.district || !formSchool.address) {
        toast.error('Please fill in all required fields');
        return;
      }

      await dataService.createSchool({
        name: formSchool.name,
        district: formSchool.district,
        address: formSchool.address,
        logo: formSchool.logo || undefined
      });

      await fetchData();
      setShowAddModal(false);
      resetForm();
      toast.success('School added successfully');
    } catch (error) {
      console.error('Error adding school:', error);
      toast.error('Failed to add school');
    }
  };

  // Handle edit school
  const handleEditSchool = async () => {
    try {
      if (!formSchool.name || !formSchool.district || !formSchool.address) {
        toast.error('Please fill in all required fields');
        return;
      }

      await dataService.updateSchool({
        id: formSchool.id,
        name: formSchool.name,
        district: formSchool.district,
        address: formSchool.address,
        logo: formSchool.logo
      });

      await fetchData();
      setShowEditModal(false);
      setSelectedSchool(null);
      resetForm();
      toast.success('School updated successfully');
    } catch (error) {
      console.error('Error updating school:', error);
      toast.error('Failed to update school');
    }
  };

  // Handle delete school
  const handleDeleteSchool = async () => {
    if (!selectedSchool) return;
    
    try {
      await dataService.deleteSchool(selectedSchool.id);
      await fetchData();
      setShowDeleteConfirm(false);
      setSelectedSchool(null);
      toast.success('School deleted successfully');
    } catch (error) {
      console.error('Error deleting school:', error);
      toast.error('Failed to delete school');
    }
  };

  // Open edit modal
  const openEditModal = (school: School) => {
    setSelectedSchool(school);
    
    // Find the district ID that matches the district name/ID stored in the school
    let districtId = school.district;
    
    // If the district is not already an ID, try to find the matching district
    if (districts.length > 0 && !districts.some(d => d.id === school.district)) {
      // First try to find by exact name match
      const matchingDistrict = districts.find(d => d.name === school.district);
      if (matchingDistrict) {
        districtId = matchingDistrict.id;
      }
    }
    
    setFormSchool({
      id: school.id,
      name: school.name,
      district: districtId,
      address: school.address,
      logo: school.logo || ''
    });
    setShowEditModal(true);
  };

  // Open delete confirmation
  const openDeleteConfirm = (school: School) => {
    setSelectedSchool(school);
    setShowDeleteConfirm(true);
  };

  // Reset form
  const resetForm = () => {
    setFormSchool({
      id: '',
      name: '',
      district: '',
      address: '',
      logo: ''
    });
  };

  // Handle search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Filter schools based on search term and import status
  const getFilteredSchools = () => {
    // First filter by search term
    const searchFiltered = schools.filter(school => {
      const searchLower = searchTerm.toLowerCase();
      return (
        school.name.toLowerCase().includes(searchLower) ||
        school.district.toLowerCase().includes(searchLower)
      );
    });
    
    // Then filter by import status if needed
    if (filter === 'all') return searchFiltered;
    
    return searchFiltered.filter(school => {
      // Find the most recent import for this school
      const schoolImports = csvImports.filter(imp => 
        imp.type === 'Schools' && imp.filename.includes(school.id)
      );
      
      if (schoolImports.length === 0) return filter === 'error'; // No imports is considered an error state
      
      const latestImport = schoolImports.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )[0];
      
      return filter === 'error' ? 
        (latestImport.status === 'failed' || latestImport.status === 'partial') : 
        (latestImport.status === 'success');
    });
  };
  
  const filteredSchools = getFilteredSchools();

  // Format timestamp to readable date/time
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Handle file upload for CSV import
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setImportStatus('uploading');
      await dataService.triggerCsvImport(type, file);
      setImportStatus('success');
      
      // Refresh CSV imports list
      const importsData = await dataService.getCsvImports();
      setCsvImports(importsData.filter(imp => 
        imp.type === 'Schools' || imp.type === 'Teachers' || imp.type === 'Students'
      ));
      
      // Clear status after 3 seconds
      setTimeout(() => setImportStatus(null), 3000);
    } catch (error) {
      console.error('Error uploading CSV:', error);
      setImportStatus('error');
      
      // Clear status after 3 seconds
      setTimeout(() => setImportStatus(null), 3000);
    }
  };

  // Get status badge with appropriate color
  const getStatusBadge = (status: string) => {
    let bgColor = '';
    let textColor = '';
    
    switch(status) {
      case 'success':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'partial':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'failed':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      case 'processing':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        break;
      default:
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
    }
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <PageContainer title="School Management" userRole="SuperAdmin">
      
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-5">
        <ul className="flex flex-wrap -mb-px">
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('overview')}
              className={`inline-block py-2 px-4 text-sm font-medium ${activeTab === 'overview' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent'}`}
            >
              School Overview
            </button>
          </li>
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('import')}
              className={`inline-block py-2 px-4 text-sm font-medium ${activeTab === 'import' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent'}`}
            >
              Import
            </button>
          </li>
        </ul>
      </div>
      
      {/* School Overview Tab */}
      {activeTab === 'overview' && (
        <div className="mb-8">
          <Card className="bg-white shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <div className="relative w-64">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                  </svg>
                </div>
                <input 
                  type="search" 
                  className="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-primary focus:border-primary" 
                  placeholder="Search schools..." 
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
              <button 
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
              >
                Add School
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="overflow-x-auto relative shadow-md sm:rounded-lg">
                <table className="w-full text-sm text-left text-gray-500">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3 px-6">School ID</th>
                      <th scope="col" className="py-3 px-6">School Name</th>
                      <th scope="col" className="py-3 px-6">District</th>
                      <th scope="col" className="py-3 px-6">Address</th>
                      <th scope="col" className="py-3 px-6">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSchools.map((school) => (
                      <tr key={school.id} className="bg-white border-b hover:bg-gray-50">
                        <td className="py-4 px-6">{school.id}</td>
                        <td className="py-4 px-6">
                          <button 
                            onClick={() => openEditModal(school)}
                            className="font-medium text-primary hover:underline"
                          >
                            {school.name}
                          </button>
                        </td>
                        <td className="py-4 px-6">
                          {districts.find(d => d.id === school.district)?.name || school.district}
                        </td>
                        <td className="py-4 px-6">{school.address}</td>
                        <td className="py-4 px-6 flex space-x-2">
                          <button 
                            onClick={() => openEditModal(school)}
                            className="font-medium text-primary hover:underline"
                          >
                            Edit
                          </button>
                          <button 
                            onClick={() => openDeleteConfirm(school)}
                            className="font-medium text-red-600 hover:underline"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}
      
      {/* Import Tab */}
      {activeTab === 'import' && (
        <div className="mb-8">
          <Card className="bg-white shadow-sm">
            <h2 className="text-xl font-bold text-gray-800 mb-4">CSV Import Management</h2>
          
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold mb-2">Schools Import</h3>
                <p className="text-sm text-gray-600 mb-4">Upload a CSV file with school data.</p>
                <div className="flex items-center">
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    id="schools-csv"
                    onChange={(e) => handleFileUpload(e, 'Schools')}
                  />
                  <label
                    htmlFor="schools-csv"
                    className="bg-primary text-white px-4 py-2 rounded cursor-pointer hover:bg-primary-dark"
                  >
                    Select File
                  </label>
                  {importStatus === 'uploading' && <span className="ml-2">Uploading...</span>}
                  {importStatus === 'success' && <span className="ml-2 text-green-600">Success!</span>}
                  {importStatus === 'error' && <span className="ml-2 text-red-600">Error!</span>}
                </div>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold mb-2">Teachers Import</h3>
                <p className="text-sm text-gray-600 mb-4">Upload a CSV file with teacher data.</p>
                <div className="flex items-center">
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    id="teachers-csv"
                    onChange={(e) => handleFileUpload(e, 'Teachers')}
                  />
                  <label
                    htmlFor="teachers-csv"
                    className="bg-primary text-white px-4 py-2 rounded cursor-pointer hover:bg-primary-dark"
                  >
                    Select File
                  </label>
                </div>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold mb-2">Students Import</h3>
                <p className="text-sm text-gray-600 mb-4">Upload a CSV file with student data.</p>
                <div className="flex items-center">
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    id="students-csv"
                    onChange={(e) => handleFileUpload(e, 'Students')}
                  />
                  <label
                    htmlFor="students-csv"
                    className="bg-primary text-white px-4 py-2 rounded cursor-pointer hover:bg-primary-dark"
                  >
                    Select File
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <h3 className="font-semibold mb-2">Recent Imports</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-500">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3">Type</th>
                      <th scope="col" className="px-6 py-3">Filename</th>
                      <th scope="col" className="px-6 py-3">Status</th>
                      <th scope="col" className="px-6 py-3">Timestamp</th>
                      <th scope="col" className="px-6 py-3">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvImports.map(imp => (
                      <tr key={imp.id} className="bg-white border-b hover:bg-gray-50">
                        <td className="px-6 py-4">{imp.type}</td>
                        <td className="px-6 py-4">{imp.filename}</td>
                        <td className="px-6 py-4">
                          {getStatusBadge(imp.status)}
                        </td>
                        <td className="px-6 py-4">{formatTimestamp(imp.timestamp)}</td>
                        <td className="px-6 py-4">
                          <button className="font-medium text-primary hover:underline">
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </div>
      )}
      
      {/* Add School Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Add New School</h2>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                School Name *
              </label>
              <input
                id="name"
                type="text"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={formSchool.name}
                onChange={(e) => setFormSchool({...formSchool, name: e.target.value})}
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="add-district">
                District *
              </label>
              <select
                id="add-district"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={formSchool.district}
                onChange={(e) => setFormSchool({...formSchool, district: e.target.value})}
                required
              >
                <option value="">Select a district</option>
                {districts.map(district => (
                  <option key={district.id} value={district.id}>{district.name}</option>
                ))}
              </select>
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="address">
                Address *
              </label>
              <input
                id="address"
                type="text"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={formSchool.address}
                onChange={(e) => setFormSchool({...formSchool, address: e.target.value})}
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="logo">
                Logo URL
              </label>
              <input
                id="logo"
                type="text"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={formSchool.logo || ''}
                onChange={(e) => setFormSchool({...formSchool, logo: e.target.value})}
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                onClick={() => setShowAddModal(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                onClick={handleAddSchool}
                type="button"
                disabled={!formSchool.name || !formSchool.district || !formSchool.address}
              >
                Add School
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit School Modal */}
      {showEditModal && selectedSchool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit School</h2>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="edit-id">
                School ID
              </label>
              <input
                id="edit-id"
                type="text"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-500 bg-gray-100 leading-tight focus:outline-none"
                value={formSchool.id}
                disabled
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="edit-name">
                School Name *
              </label>
              <input
                id="edit-name"
                type="text"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={formSchool.name}
                onChange={(e) => setFormSchool({...formSchool, name: e.target.value})}
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="edit-district">
                District *
              </label>
              <select
                id="edit-district"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={formSchool.district}
                onChange={(e) => setFormSchool({...formSchool, district: e.target.value})}
                required
              >
                <option value="">Select a district</option>
                {districts.map(district => (
                  <option key={district.id} value={district.id}>{district.name}</option>
                ))}
              </select>
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="edit-address">
                Address *
              </label>
              <input
                id="edit-address"
                type="text"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={formSchool.address}
                onChange={(e) => setFormSchool({...formSchool, address: e.target.value})}
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="edit-logo">
                Logo URL
              </label>
              <input
                id="edit-logo"
                type="text"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={formSchool.logo || ''}
                onChange={(e) => setFormSchool({...formSchool, logo: e.target.value})}
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                onClick={() => setShowEditModal(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                onClick={handleEditSchool}
                type="button"
                disabled={!formSchool.name || !formSchool.district || !formSchool.address}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedSchool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Confirm Delete</h2>
            <p className="mb-6">Are you sure you want to delete the school "{selectedSchool.name}"? This action cannot be undone.</p>
            
            <div className="flex justify-end space-x-2">
              <button
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                onClick={() => setShowDeleteConfirm(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                onClick={handleDeleteSchool}
                type="button"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default SuperAdminSchoolManagementScreen;
